import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useAppStore } from '@/store'
import { cloneCanonicalTroubleshootingDraft } from '../canonicalTroubleshootingDraft'
import { mergeFollowUpDraft } from '../followUpPlan'
import { v5 as uuidv5 } from 'uuid'

import {
  extractDocumentText,
  approveWorkflowRun,
  cancelWorkflowRun,
  checkTroubleshootingReadiness,
  createWorkflowConversation,
  fetchDocumentRecipeText,
  generateWorkflowDraft,
  getWorkflowEngine,
  getWorkflowRun,
  getWorkflowRunSteps,
  getWorkflowTemplateDefinition,
  listWorkflowTemplates,
  previewWorkflowTemplate,
  publishWorkflowTemplate,
  resumeWorkflowRun,
  retryWorkflowRun,
  startWorkflowRun,
  subscribeWorkflowRunProgress,
  unpauseWorkflowRun,
  upsertWorkflowDraft,
  type WorkflowProgressSubscription,
  type WorkflowTemplateRow,
} from '../workflowApi'
import type {
  GenerateWorkflowDraftResponse,
  WorkflowClarifyingQuestion,
  WorkflowDefinition,
  WorkflowTemplateSuggestion,
  NormalizedToolCall,
  StartWorkflowRunRequest,
  WorkflowGraphNodeData,
  WorkflowPlanPreview,
  WorkflowRunStatus,
  WorkflowResolvedSources,
  WorkflowRunView,
  WorkflowStepRunView,
} from '../workflowTypes'
import {
  buildTimeWindow,
  buildWorkflowScope,
  isInvalidTimestamp,
} from '../workflowScope'

export const TROUBLESHOOTING_TERMINAL_STATUSES: ReadonlySet<WorkflowRunStatus> =
  new Set([
    'completed',
    'completed_with_unknowns',
    'blocked',
    'budget_limited',
    'guardrail_stopped',
    'failed',
    'cancelled',
  ])

/**
 * Idempotency key for a builder-started run. Deliberately EXCLUDES the
 * conversation id: every Run click creates a fresh conversation, so a
 * conversation-scoped key could never dedupe the panic second click — each
 * click minted a new key and a duplicate run (seen live when a frozen backend
 * hid the first click's run for 16s). Instead the key covers the FULL run
 * input — template, whole problem text, scope, time window, verification —
 * as a hash (greptile #25726: a truncated-problem key silently collided
 * distinct requests) within a coarse time bucket, plus this builder mount's
 * session so Create-workflow + the same question cannot join a parked run
 * from the last page. An identical retry inside the window returns the FIRST
 * run (the backend exact-matches on (tenant, user, key)); anything different,
 * or a deliberate re-run in a later bucket, starts fresh. Clicks straddling a
 * bucket boundary don't dedupe — accepted; this is best-effort protection,
 * not a lock. Terminal dedupe hits are handled at the call site (startRun
 * re-issues with a salt) so a quick complete/cancel never blocks an
 * intentional rerun.
 */
export const RUN_IDEMPOTENCY_BUCKET_MS = 120_000
export interface RunIdempotencyInput {
  templateId: string
  problem: string
  scope?: unknown
  timeWindow?: unknown
  verification?: boolean
  /**
   * This builder mount. Create-workflow opens a new mount, so the same
   * question cannot join a parked run from the previous page. Stable for the
   * panic double-click (same mount, same key).
   */
  session?: string
}
/** FNV-1a 32-bit — tiny, deterministic, non-crypto (collision here only means
 *  an extra dedupe hit, which the terminal-retry at the call site absorbs). */
function fnv1a(text: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(36)
}
/** Key-order-canonical stringify: plain objects serialize with sorted keys
 *  (arrays keep order) so two structurally-equal scope/timeWindow objects
 *  built with different insertion order fingerprint identically
 *  (khangngtien review on #25726). */
function canonicalStringify(value: unknown): string {
  return JSON.stringify(value, (_key, val: unknown) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const record = val as Record<string, unknown>
      return Object.keys(record)
        .sort()
        .reduce<Record<string, unknown>>((out, k) => {
          out[k] = record[k]
          return out
        }, {})
    }
    return val
  })
}
export function buildRunIdempotencyKey(
  input: RunIdempotencyInput,
  now: number = Date.now(),
  /** Distinguishes a deliberate rerun after a terminal dedupe hit (startRun's retry). */
  salt = ''
): string {
  const bucket = Math.floor(now / RUN_IDEMPOTENCY_BUCKET_MS)
  const fingerprint = fnv1a(
    canonicalStringify([
      input.templateId,
      input.problem,
      input.scope ?? null,
      input.timeWindow ?? null,
      input.verification === true,
      input.session ?? '',
    ])
  )
  // Well inside the backend's varchar(128) for any input.
  return `workflow-builder:${bucket}:${fingerprint}${salt ? `:${salt}` : ''}`
}

export type TroubleshootingSetupMode = 'configuration' | 'workflow_chat'
export type TroubleshootingContextType = 'workspace' | 'document'

/**
 * Namespace for transcript document ids — must equal `TRANSCRIPT_DOC_NS` in
 * `backend/web/src/transcript-artifact/transcript-artifact.service.ts`, which mints the artifact
 * as `uuidv5(meetingToken, TRANSCRIPT_DOC_NS)`. Duplicated rather than imported because the
 * frontend does not build against the Nest tier; a test pins a known vector so the two cannot
 * drift silently.
 */
const TRANSCRIPT_DOC_NS = '6b1c0f4e-2d3a-4f9b-9c2e-7a5d8e1f0b34'

export function useTroubleshootingWorkflow({ active }: { active: boolean }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const runId = active ? searchParams.get('runId') : null
  // The workflow opened from the list (?templateId=...), if any — the raw value
  // (null when absent). This is the AUTHORITATIVE identity for Save: saving
  // versions the workflow you opened, regardless of which run/built-in executed.
  const openedTemplateId = active ? searchParams.get('templateId') : null
  // Effective template the draft previews and the run executes. Defaults to the
  // base when nothing was opened; never use this for save identity (use openedTemplateId).
  const templateId = openedTemplateId || 'troubleshooting_base'
  /**
   * Stable for this builder mount only — Create workflow must not join the last run.
   *
   * LAZY, because `useRef(expr)` evaluates `expr` on EVERY render and throws all but the first
   * away. Harmless for this one — a template literal and a `Math.random()` — but the discarded
   * value is a fresh session key each render, so the shape reads as if the key could change and
   * it costs work on every render to prove that it does not (review). A `useState` initialiser
   * runs once by contract; the setter is never called, so nothing re-renders and this stays the
   * mount-scoped constant it was.
   */
  const [builderSessionKey] = useState(
    () => `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
  )
  const boardList = useAppStore((state) => state.boardList)
  const loadBoardList = useAppStore((state) => state.loadBoardList)
  const createBoard = useAppStore((state) => state.createBoard)
  const generateDefaultBoardName = useAppStore(
    (state) => state.generateDefaultBoardName
  )
  const setAppContextId = useAppStore((state) => state.setContextId)

  const [problem, setProblem] = useState(
    'Reactor outlet temperature is 10 C high since last night'
  )
  const [workflowPrompt, setWorkflowPrompt] = useState('')
  const [existingConversationId, setExistingConversationId] = useState('')
  const [contextId, setContextId] = useState('')
  const [contextType, setContextType] =
    useState<TroubleshootingContextType>('workspace')
  const [timeWindowStart, setTimeWindowStart] = useState('')
  const [timeWindowEnd, setTimeWindowEnd] = useState('')
  const [scopeTags, setScopeTags] = useState('')
  const [scopeEquipment, setScopeEquipment] = useState('')
  const [scopeDocumentIds, setScopeDocumentIds] = useState('')
  const [scopeBoardId, setScopeBoardId] = useState('')
  // "Evidence verification" run toggle: append the adversarial verify-diagnosis
  // gate to a troubleshooting_base run (backend composeVerification). Off by
  // default — opt-in "deeper" pass. Ignored by the backend on other templates.
  const [verification, setVerification] = useState(false)
  /**
   * WHICH ENGINE a run started now would use, from the backend's own flag read.
   *
   * Asked rather than evaluated client-side because the frontend's PostHog identity is not the
   * backend's. `null` until the answer lands. Send no longer branches on this: both engines
   * draft first, and the generate-draft route draws v1 or the v2 plan from the same flag.
   */
  const [engine, setEngine] = useState<'v1' | 'v2' | null>(null)
  const readEngine = useCallback(async (): Promise<'v1' | 'v2'> => {
    // try/catch around the CALL, not only the promise: a test that mocks `workflowApi` without this
    // function would otherwise throw a TypeError out of an effect and take the builder down. Any
    // failure lands on 'v1', the branch that is safe to take by mistake.
    try {
      const res = await getWorkflowEngine()
      return res.engine === 'v2' ? 'v2' : 'v1'
    } catch {
      return 'v1'
    }
  }, [])
  const refreshEngine = useCallback(async (): Promise<'v1' | 'v2'> => {
    const next = await readEngine()
    setEngine(next)
    return next
  }, [readEngine])
  useEffect(() => {
    if (!active) return
    let mounted = true
    // Re-fetched on every active mount, including tab remount. The GET is one cheap
    // round-trip so the canvas/engine snapshot is not a session-stale value.
    void (async () => {
      const next = await readEngine()
      if (mounted) setEngine(next)
    })()
    return () => {
      mounted = false
    }
  }, [active, readEngine])
  const [readinessWarnings, setReadinessWarnings] = useState<string[]>([])
  /**
   * The sources a run resolved, TAGGED with the run that resolved them. The tag is
   * the whole point: this is fed by the progress stream, and a bare value survived
   * every run transition — starting a new run, or switching the loaded run via
   * `?runId=` ("View Run") — so a review gate could show the PREVIOUS run's P&IDs
   * and tags (Khang on #27076). Reading it back through the current run id makes a
   * mismatch unrepresentable, which a reset in `startRun`/`loadRun` would not: those
   * run on every SSE frame's refresh, and clearing there would erase the sources the
   * same frame just delivered.
   */
  const [resolvedSourcesEntry, setResolvedSourcesEntry] = useState<{
    runId: string
    sources: WorkflowResolvedSources
  } | null>(null)
  const [draft, setDraft] = useState<GenerateWorkflowDraftResponse | null>(null)
  // Whether the current `draft` is a genuinely authored workflow (NL / document
  // generation) vs. a prebuilt preset preview or the canonical fallback shown
  // when generation fails. Only authored drafts get persisted on Run; the others
  // are built-ins and must run by id — the fallback's definition is a display-
  // only skeleton (no step instructions) that the validator rightly rejects.
  const [draftIsAuthored, setDraftIsAuthored] = useState(false)
  // Published-ness of the OPENED workflow (?templateId), from hydration:
  // true = published or built-in, false = never-published draft, null =
  // unknown (fresh builder, or a run restore where it must have published).
  const [openedPublished, setOpenedPublished] = useState<boolean | null>(null)
  const [planPreview, setPlanPreview] = useState<WorkflowPlanPreview | null>(
    null
  )
  const [run, setRun] = useState<WorkflowRunView | null>(null)
  // The URL names a run that turned out not to exist (404) — releases
  // runPending so a stale/deleted ?runId can't fake an in-flight run forever.
  const [runMissing, setRunMissing] = useState(false)
  const [steps, setSteps] = useState<WorkflowStepRunView[]>([])
  const [selectedToolCall, setSelectedToolCall] =
    useState<NormalizedToolCall | null>(null)
  const [selectedGraphNode, setSelectedGraphNode] =
    useState<WorkflowGraphNodeData | null>(null)
  const [clarificationAnswer, setClarificationAnswer] = useState('')
  const [loading, setLoading] = useState(false)
  const [starting, setStarting] = useState(false)
  const [resuming, setResuming] = useState(false)
  // Bumped after a resume/unpause so the live-progress effect tears down and
  // re-subscribes — the SSE stream closes when a run parks (waiting_for_user)
  // and must reopen once the run is resumed, else the UI freezes at the gate.
  const [liveNonce, setLiveNonce] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const loadRun = useCallback(async (id: string) => {
    const [nextRun, nextSteps] = await Promise.all([
      getWorkflowRun(id),
      getWorkflowRunSteps(id),
    ])
    if (!nextRun) return null
    setRun(nextRun)
    setSteps(Array.isArray(nextSteps?.steps) ? nextSteps.steps : [])
    setPlanPreview(nextRun.planPreview)
    return nextRun
  }, [])

  const refreshRun = useCallback(async () => {
    if (!runId) return
    setError(null)
    try {
      await loadRun(runId)
    } catch (err) {
      setError(errorMessage(err))
    }
  }, [loadRun, runId])

  // Opening a saved workflow with NO run to restore (?templateId only — e.g. a
  // chat-built draft that generation auto-saved but the user never ran): load
  // its definition + plan so the canvas shows the saved workflow instead of a
  // blank builder. A run restore (?runId) supersedes this — the run carries its
  // own graph/plan. Fires once per opened id.
  const hydratedTemplateRef = useRef<string | null>(null)
  // Content fingerprint of the last definition THIS session persisted+published
  // via Run — re-running unchanged content skips the persist+publish leg
  // instead of forking a pointless new version over the immutable published row.
  const lastPublishedRef = useRef<{
    templateId: string
    fingerprint: string
    version: string
  } | null>(null)
  useEffect(() => {
    if (!active || !openedTemplateId || runId) return
    if (hydratedTemplateRef.current === openedTemplateId) return
    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const [definition, planPreview, list] = await Promise.all([
          getWorkflowTemplateDefinition(openedTemplateId),
          previewWorkflowTemplate(openedTemplateId, { mode: 'lite' }),
          // Builtin-ness decides run identity below; degrade to "unknown"
          // (treated as builtin) rather than failing the whole open.
          listWorkflowTemplates().catch(() => ({ templates: [] })),
        ])
        if (cancelled) return
        // Mark hydrated only when the results APPLY. StrictMode's dev
        // double-mount cancels the first run's fetch; marking up-front made
        // the second run early-return and discard everything — the opened
        // draft stayed "Untitled" over a blank canvas while the network tab
        // showed perfectly good 200s.
        hydratedTemplateRef.current = openedTemplateId
        setDraft({
          definition,
          planPreview,
          validation: { ok: true },
          sourceTemplateId: openedTemplateId,
        })
        setPlanPreview(planPreview)
        // A tenant-authored row (draft or published) re-persists + publishes on
        // Run; a built-in must keep running by id (never fork a shadow copy).
        // The list carries built-ins + PUBLISHED tenant rows — a never-published
        // draft is absent, so "missing" means tenant draft, NOT built-in.
        // (Defaulting missing→builtin made Run start a draft by id, which the
        // published-only run resolver rejected: Unknown workflow "<id>".)
        const summary = list.templates.find((t) => t.id === openedTemplateId)
        setDraftIsAuthored(!(summary?.builtin ?? false))
        setOpenedPublished(Boolean(summary))
      } catch (err) {
        if (!cancelled) setError(errorMessage(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [active, openedTemplateId, runId])

  const ensureWorkflowWorkspaceContext = useCallback(async (): Promise<
    string | null
  > => {
    // The builder should default to the tenant catalog KG. The global app
    // context may be any workspace the user visited before opening
    // /workflow/new. FS-553 removed the per-user Master Board (and its store
    // action + endpoints), so resolve to the user's most recent workspace —
    // and, since Start is an explicit start action, create the user's first
    // normal board on demand when they have none (same pattern as AIChat /
    // LLMCallDropdown / CallConfigModal).
    let workspaces = boardList
    if (workspaces.length === 0) {
      await loadBoardList()
      workspaces = useAppStore.getState().boardList
    }

    if (workspaces.length > 0) {
      const [latestWorkspace] = [...workspaces].sort((a, b) => {
        const timeB = b.created_at ? new Date(b.created_at).getTime() : 0
        const timeA = a.created_at ? new Date(a.created_at).getTime() : 0
        return timeB - timeA
      })
      if (latestWorkspace?.id) {
        setAppContextId(latestWorkspace.id)
        return latestWorkspace.id
      }
    }

    const created = await createBoard(generateDefaultBoardName())
    if (created?.id) {
      setAppContextId(created.id)
      return created.id
    }

    return null
  }, [
    boardList,
    createBoard,
    generateDefaultBoardName,
    loadBoardList,
    setAppContextId,
  ])

  useEffect(() => {
    if (!active) return
    if (!runId) {
      setRun(null)
      setSteps([])
      setError(null)
      setRunMissing(false)
      return
    }
    setRunMissing(false)

    const activeRunId = runId
    let cancelled = false
    let interval: ReturnType<typeof setInterval> | undefined
    // Re-entrancy guard: on a slow/hung backend a 2s tick must SKIP, not
    // stack — 26 piled-up GETs against an already-starved pod is how a
    // degraded minute becomes a degraded hour.
    let refreshing = false
    // 404-terminal latch: once the run is known-gone, NOTHING may restart
    // polling — not even the SSE stream's onClose('error'), which fires when
    // its own connection to the nonexistent run fails.
    let missing = false

    async function refresh() {
      if (refreshing) return
      refreshing = true
      setError(null)
      try {
        const loaded = await loadRun(activeRunId)
        if (cancelled || !loaded) return
        // Stop polling ONLY on a genuine terminal status. Parked states
        // (waiting_for_user / awaiting_approval / paused) must keep polling:
        // a resume/approve executes the next steps SYNCHRONOUSLY server-side
        // (and closes the SSE stream while parked), so without a live poll the
        // UI freezes at the gate until a manual reload. Polling a parked run is
        // one cheap GET / 2s and the next tick after the user resumes streams
        // the step completions straight into the UI.
        if (TROUBLESHOOTING_TERMINAL_STATUSES.has(loaded.status)) {
          if (interval) {
            clearInterval(interval)
            interval = undefined
          }
        } else {
          startPolling()
        }
      } catch (err) {
        if (cancelled) return
        setError(errorMessage(err))
        if (httpStatus(err) === 404) {
          // The run genuinely doesn't exist (deleted, or a stale URL) —
          // retrying can't fix it, and runPending must release the UI. Also
          // drop any previously-loaded snapshot: a mid-run 404 must not leave
          // a dead run rendering as "Running…" forever. The latch + closing
          // the stream keep the SSE onClose('error') callback from quietly
          // restarting the poll against the dead run.
          missing = true
          setRunMissing(true)
          setRun(null)
          setSteps([])
          if (interval) {
            clearInterval(interval)
            interval = undefined
          }
          subscription.close()
          return
        }
        // Transient failure (a starved pod 500ing, a network blip): the run
        // is very likely STILL EXECUTING server-side. Keep polling so the UI
        // self-heals instead of silently dropping a live run — a failed FIRST
        // load used to leave this effect inert forever, which read as "the
        // run stopped" and invited a duplicate click of Run.
        startPolling()
      } finally {
        refreshing = false
      }
    }

    function startPolling() {
      if (cancelled || missing || interval) return
      interval = setInterval(refresh, 2_000)
    }

    // Live updates over SSE: each frame triggers a FULL reload so the rich step
    // outputs the lightweight frame omits stay current. Fall back to 2s polling
    // only if the stream can't be established or drops mid-run (the subscriber
    // itself does not poll — that policy lives here with the data model).
    const subscription: WorkflowProgressSubscription = subscribeWorkflowRunProgress(activeRunId, {
      onFrame: (frame) => {
        // The triage already resolved which drawings and tags the run will use; the
        // only way to see it was to click into a step. Keep it so the chat can ask
        // "is this the right data?" before the analysis (FS item 3 / Khang's payload).
        if (frame?.resolvedSources) {
          setResolvedSourcesEntry({ runId: activeRunId, sources: frame.resolvedSources })
        }
        void refresh()
      },
      onClose: (reason) => {
        if (reason === 'error') startPolling()
      },
    })

    // Paint immediately rather than waiting for the first frame.
    refresh()

    return () => {
      cancelled = true
      subscription?.close()
      if (interval) clearInterval(interval)
    }
  }, [active, loadRun, runId, liveNonce])

  const generateDraft = useCallback(
    async (
      mode: TroubleshootingSetupMode,
      promptOverride?: string,
      options?: {
        /** Answers to a prior clarify round (re-generation call). */
        clarificationAnswers?: Array<{ question: string; answer: string }>
        /** Load THIS registered template instead of generating (template ask → Yes). */
        presetHint?: string
        /** Skip clarify/reuse and generate. Blank builder declined tenant reuse. */
        forceGenerate?: boolean
      }
    ): Promise<{
      draft: GenerateWorkflowDraftResponse | null
      generated: boolean
      /** Cursor-style ask: answer these and re-call with clarificationAnswers. */
      questions?: WorkflowClarifyingQuestion[]
      /** Section-9 template ask: offer this template before generating. */
      templateSuggestion?: WorkflowTemplateSuggestion
    }> => {
      setLoading(true)
      setError(null)
      try {
        // Configuration mode = configure-and-run the selected prebuilt template:
        // fetch its plan by id. Chat mode = real NL authoring when a prompt is
        // given (no preset, so the backend generator runs); otherwise preview
        // the selected/base template.
        //
        // `promptOverride` matters: send() sets workflowPrompt and calls
        // generateDraft in the same tick, so this closure's `workflowPrompt` is
        // still stale (empty on the first turn). Reading it routed every custom
        // chat prompt to the canonical preset shortcut instead of NL generation —
        // which is why "Build me an FMEA workflow" silently returned the static
        // troubleshooting_base. Prefer the caller's prompt when supplied.
        const prompt = (promptOverride ?? workflowPrompt).trim()
        // NL authoring (prompt present) mints a NEW workflow to persist; a
        // preset fetch just previews a prebuilt template (run it by id).
        const nlAuthoring = mode === 'workflow_chat' && Boolean(prompt)
        const answers = options?.clarificationAnswers ?? []
        const body = options?.presetHint
          ? // Template ask → Yes: fetch the suggested template by id.
            { mode, presetHint: options.presetHint }
          : nlAuthoring
            ? {
                mode,
                prompt,
                // Create-workflow (`/workflow/new`, no templateId): the same
                // question must not reopen the last tenant workflow.
                ...(!openedTemplateId ? { reuseExisting: false as const } : {}),
                // Cursor-style ask: let the triage pass return questions on the
                // FIRST call; a re-call carries the answers instead.
                ...(options?.forceGenerate
                  ? {}
                  : answers.length > 0
                    ? { clarificationAnswers: answers }
                    : { clarify: true }),
              }
            : { mode, presetHint: templateId }
        const response = await generateWorkflowDraft(body)
        if ('clarifyingQuestions' in response) {
          // No draft yet — the chat renders the questions and re-calls.
          return {
            draft: null,
            generated: false,
            questions: response.clarifyingQuestions,
            ...(response.templateSuggestion
              ? { templateSuggestion: response.templateSuggestion }
              : {}),
          }
        }
        // A FOLLOW-UP CONTINUES THE DIAGRAM. Assigning the response wholesale threw away the
        // canvas the person was reading and redrew it — usually with the same steps in a new
        // order under a new name — so a related follow-up looked like a different workflow
        // (feedback doc item 11). `mergeFollowUpDraft` appends only the genuinely new steps when
        // the two plans overlap, and still replaces outright when they share nothing (asking for
        // a different workflow does give you a different one). Chat authoring only: a preset
        // fetch is a deliberate "show me THAT template".
        const continued =
          nlAuthoring && !options?.presetHint ? mergeFollowUpDraft(draft, response) : response
        setDraft(continued)
        // A v2-previewed canvas is the engine's plan, not an authored v1 definition — persisting
        // it would write a carrier the v1 validator does not mean, and Run would publish it.
        const persistAuthored = nlAuthoring && continued.drawnBy !== 'v2'
        setDraftIsAuthored(persistAuthored)
        setPlanPreview(continued.planPreview)
        if (persistAuthored && !options?.presetHint) {
          // Auto-save every chat-built workflow as a DRAFT row immediately —
          // the generator already minted its name — so closing the tab can't
          // lose it (it shows in the library with the Draft pill). Upsert, so
          // a regenerated draft replaces its earlier auto-save at the same
          // id@version instead of silently losing to it (a published version
          // there makes the server fork the next version). Built-in previews /
          // template-ask picks are NOT copied (deploy versions them
          // deliberately). Fire-and-forget: a failed save must not break the
          // generation UX; running/Save-as-draft retries persistence later.
          void upsertWorkflowDraft(response.definition)
            .then((row) => {
              // The server may have minted a NEW version (fork over a
              // published one) — mirror it so later saves hit that row.
              if (row?.version && row.version !== response.definition.version) {
                setDraft((prev) =>
                  prev && prev.definition.id === response.definition.id
                    ? { ...prev, definition: { ...prev.definition, version: row.version } }
                    : prev
                )
              }
            })
            .catch(() => {})
        }
        // Same object setDraft just stored. Returning `response` handed the chat the
        // unmerged picture while the canvas showed the continuation.
        return { draft: continued, generated: true }
      } catch (err) {
        // Surface the real cause in the error banner — a 401/403/500 or a network
        // timeout is NOT "rephrase your prompt", and masking it nudges the user
        // the wrong way. We still keep the bundled canonical as a usable starting
        // point, and flag generated:false so the caller doesn't claim it generated
        // a custom workflow (the old code lied "generated from the catalog KG").
        setError(errorMessage(err))
        const fallback = cloneCanonicalTroubleshootingDraft()
        setDraft(fallback)
        // The canonical fallback is a display-only built-in skeleton — not an
        // authored workflow. Run it by id (troubleshooting_base), never persist.
        setDraftIsAuthored(false)
        setPlanPreview(fallback.planPreview)
        return { draft: fallback, generated: false }
      } finally {
        setLoading(false)
      }
    },
    // `draft` joins the deps because the merge READS it: a stale closure would merge the
    // follow-up into the plan from two turns ago and lose the step in between.
    [workflowPrompt, templateId, openedTemplateId, draft]
  )

  // Recipe source 2: generate a workflow from a process document. Fetches the
  // document's leading text and hands it to the backend's quarantined extractor.
  const generateDraftFromDocument = useCallback(
    async (documentId: string): Promise<GenerateWorkflowDraftResponse | null> => {
      setLoading(true)
      setError(null)
      try {
        const documentText = await fetchDocumentRecipeText(documentId)
        if (!documentText) {
          setError('Could not read text from the selected document.')
          return null
        }
        const response = await generateWorkflowDraft({ mode: 'workflow_chat', documentText })
        if ('clarifyingQuestions' in response) {
          // Document extraction never asks; treat an unexpected ask as a miss.
          setError('Could not generate a workflow from the document.')
          return null
        }
        setDraft(response)
        setDraftIsAuthored(true)
        setPlanPreview(response.planPreview)
        // Same auto-save as chat generation: a document-extracted workflow is
        // authored work — persist the draft row so it can't be lost.
        void upsertWorkflowDraft(response.definition).catch(() => {})
        return response
      } catch (err) {
        setError(errorMessage(err))
        return null
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const startRunImpl = useCallback(async (overrides?: { problem?: string }) => {
    // `overrides.problem` is what a chat send passes: it calls setProblem and
    // startRun in the SAME tick, so the `problem` state below is still the
    // previous value (or the placeholder) at this point — the run would carry
    // the wrong question. Every other caller (the Run pill, the canvas node)
    // has no in-flight edit and reads state as before.
    const trimmedProblem = (overrides?.problem ?? problem).trim()
    if (!trimmedProblem) {
      setError('Problem is required.')
      return false
    }

    setStarting(true)
    setError(null)
    // Clear warnings from a previous attempt up front — early returns below
    // (missing workspace, conversation-create failure) never reach the
    // readiness call that would otherwise overwrite them.
    setReadinessWarnings([])
    setSelectedGraphNode(null)
    setSelectedToolCall(null)
    try {
      const suppliedConversationId = existingConversationId.trim()
      const explicitContextId = contextId.trim()
      let conversationContextId = explicitContextId
      let conversationContextType = contextType
      if (!suppliedConversationId && !conversationContextId) {
        const fallbackContextId = await ensureWorkflowWorkspaceContext()
        if (!fallbackContextId) {
          setError(
            'Could not prepare a backing workspace conversation. Check that the local API is running and that your account has a workspace in this environment.'
          )
          return false
        }
        conversationContextId = fallbackContextId
        conversationContextType = 'workspace'
      }

      const conversationId =
        suppliedConversationId ||
        (await createWorkflowConversation({
          contextId: conversationContextId,
          contextType: conversationContextType,
        }))
      const readiness = await checkTroubleshootingReadiness(trimmedProblem)
      setReadinessWarnings([
        ...readiness.warnings,
        ...(!readiness.ready && readiness.reason ? [readiness.reason] : []),
      ])

      const scope = buildWorkflowScope({
        contextType,
        contextId: explicitContextId,
        tags: scopeTags,
        equipment: scopeEquipment,
        documentIds: scopeDocumentIds,
        boardId: scopeBoardId,
      })
      if (isInvalidTimestamp(timeWindowStart) || isInvalidTimestamp(timeWindowEnd)) {
        setError(
          'Time window must use ISO 8601 timestamps, e.g. 2026-06-08T08:00:00Z.'
        )
        return false
      }
      const timeWindow = buildTimeWindow(timeWindowStart, timeWindowEnd)

      // An authored draft (NL / document generation) is a NEW workflow: persist
      // + publish it so it's runnable by its own id. A non-authored draft is a
      // prebuilt preset preview or the canonical fallback shown when generation
      // failed — both are built-ins, so run them by sourceTemplateId WITHOUT
      // persisting (the fallback's definition is an instruction-less display
      // skeleton the validator rejects → "Invalid workflow definition" on Run).
      let runTemplateId = templateId
      const authoredDefinition = draft && draftIsAuthored ? draft.definition : null
      if (authoredDefinition) {
        const fingerprint = workflowContentFingerprint(authoredDefinition)
        const cached = lastPublishedRef.current
        let published = false
        if (cached?.templateId === authoredDefinition.id && cached.fingerprint === fingerprint) {
          // Unchanged content this session: skip the redundant PERSIST (a
          // re-upsert would fork a pointless new version over the immutable
          // published row) — but still RE-ASSERT publication. publish() is
          // idempotent on an already-published row, and another tab may have
          // published different content or archived this version meanwhile;
          // skipping publish outright would silently run THAT definition
          // instead of the one on this canvas (greptile on #26084).
          try {
            await publishWorkflowTemplate(cached.templateId, cached.version)
            runTemplateId = cached.templateId
            published = true
          } catch {
            // The cached row no longer exists (version deleted) — drop the
            // cache and take the full persist + publish path below.
            lastPublishedRef.current = null
          }
        }
        if (!published) {
          // Publish the version the SERVER persisted, never the in-memory one.
          // The definition's own `version` can be stale: the generator stamps
          // "1" and the real version is minted server-side, so publishing the
          // in-memory string resurrected an ANCIENT row (live incident: Run
          // published v1, a days-old 5-step workflow, over the fresh v7 draft
          // on canvas). The draft upsert owns versioning outright — it
          // overwrites the working draft or forks latest+1 over an immutable
          // row — and returns the authoritative row to publish.
          const persisted: WorkflowTemplateRow = await upsertWorkflowDraft(authoredDefinition)
          await publishWorkflowTemplate(persisted.templateId, persisted.version)
          lastPublishedRef.current = {
            templateId: persisted.templateId,
            fingerprint,
            version: persisted.version,
          }
          // Adopt the persisted definition (server-stamped id/version) so the
          // canvas state and any later save/publish agree with the store.
          if (persisted.definition) {
            const adopted = persisted.definition
            setDraft((prev) =>
              prev && prev.definition.id === persisted.templateId
                ? { ...prev, definition: adopted }
                : prev
            )
          }
          setOpenedPublished(true)
          runTemplateId = persisted.templateId
        }
      } else if (draft) {
        runTemplateId = draft.sourceTemplateId
      }

      const request: StartWorkflowRunRequest = {
        // Run the selected prebuilt template, or the just-published authored one.
        templateId: runTemplateId,
        // THE CARDS ON SCREEN. generate-draft returns compiled v2 steps with
        // toolPlan intact; without this the refuse path re-authors a DAG and
        // the pause is a picture. Never the draft `definition` — that carrier
        // strips toolPlan. Absent (v1, scripted start) v2 still plans from
        // the question.
        ...(draft?.drawnBy === 'v2' && draft.requestedPlan?.steps?.length
          ? { requestedPlan: draft.requestedPlan }
          : {}),
        input: {
          problem: trimmedProblem,
          mode: 'lite',
          ...(timeWindow ? { timeWindow } : {}),
          ...(scope ? { scope } : {}),
          ...(verification ? { verification: true } : {}),
          // This session's authoring clarify-pass answers ride the run request:
          // a REUSED template's persisted definition can't carry them (the run
          // resolves the published row), so the runner seeds its clarification
          // ledger + per-run persona from here instead. Fresh drafts also carry
          // them on the definition; the runner dedupes by question.
          ...(draft?.definition?.authoringClarifications?.length
            ? { authoringClarifications: draft.definition.authoringClarifications }
            : {}),
          kgReadiness: readiness,
        },
        // Unmatched v2 canvas: execute these compiled steps, not a second plan.
        ...(draft?.drawnBy === 'v2' && draft.compiledSteps && draft.compiledSteps.length > 0
          ? { compiledSteps: draft.compiledSteps }
          : {}),
        // Conversation-INDEPENDENT dedupe key (see buildRunIdempotencyKey):
        // every Run click creates a fresh conversation, so the old
        // conversation-scoped key never deduped the panic second click.
        idempotencyKey: buildRunIdempotencyKey({
          templateId: runTemplateId,
          problem: trimmedProblem,
          scope,
          timeWindow,
          ...(verification ? { verification: true } : {}),
          session: builderSessionKey,
        }),
      }
      let response = await startWorkflowRun(conversationId, request)
      // Dedupe exists to JOIN an in-flight run, never to replay a finished
      // one: the backend returns ANY run matching the key, so a run that
      // completed/cancelled inside the bucket would otherwise swallow a
      // deliberate rerun (greptile #25726). A terminal dedupe hit → reissue
      // once with a salted key for a genuinely fresh run.
      if (TROUBLESHOOTING_TERMINAL_STATUSES.has(response.status)) {
        response = await startWorkflowRun(conversationId, {
          ...request,
          idempotencyKey: buildRunIdempotencyKey(
            {
              templateId: runTemplateId,
              problem: trimmedProblem,
              scope,
              timeWindow,
              ...(verification ? { verification: true } : {}),
              session: builderSessionKey,
            },
            Date.now(),
            `r${Date.now().toString(36)}`
          ),
        })
      }
      // The runId effect below loads (and polls) the run once the query
      // param lands — no need for an explicit loadRun here too. Preserve the
      // opened workflow identity (?templateId) so a later Save versions THIS
      // workflow rather than forking; a fresh build carries no templateId and
      // saves as a brand-new workflow.
      const params = new URLSearchParams({ runId: response.workflowRunId })
      if (openedTemplateId) params.set('templateId', openedTemplateId)
      navigate(`/workflow/new?${params.toString()}`, {
        replace: true,
      })
      return true
    } catch (err) {
      setError(errorMessage(err))
      return false
    } finally {
      setStarting(false)
    }
  }, [
    builderSessionKey,
    contextId,
    contextType,
    draft,
    draftIsAuthored,
    ensureWorkflowWorkspaceContext,
    existingConversationId,
    navigate,
    problem,
    scopeBoardId,
    scopeDocumentIds,
    scopeEquipment,
    scopeTags,
    templateId,
    openedTemplateId,
    timeWindowEnd,
    timeWindowStart,
    verification,
  ])

  // Latest-callback indirection: the builder canvas wires `onRun` into
  // memoized node data on the assumption that startRun is a STABLE callback —
  // it was not (its deps include draft/problem/scope), so a memoized closure
  // invoked a stale startRun bound to an old draft (live incident: the stale
  // copy still held the generator-stamped version "1" and published an
  // ancient row). The exported startRun is now genuinely stable; it always
  // executes the latest implementation with the latest state.
  const startRunRef = useRef(startRunImpl)
  startRunRef.current = startRunImpl
  const startRun = useCallback(
    (overrides?: { problem?: string }) => startRunRef.current(overrides),
    []
  )

  /**
   * Open this workflow's Report tab — the same destination the library row menu uses.
   * `undefined` until a persisted workflow exists to point at, so the affordance can
   * hide itself rather than navigate nowhere (FS item 6: the report had to be
   * discovered; three people took two minutes to find the tab on the demo call).
   */
  const reportTemplateId = openedTemplateId ?? draft?.definition?.id
  const openReport = useMemo(
    () =>
      reportTemplateId
        ? (runId?: string) =>
            navigate(
              `/workflow/new?templateId=${encodeURIComponent(reportTemplateId)}&view=report` +
                // Carry the run identity when the caller has one. A finished-run card
                // in the chat scroll-back is a specific run's result; without this the
                // report opened on whichever run happens to be newest, which for a
                // scroll-back card is the WRONG one (greptile on #27044).
                (runId ? `&reportRunId=${encodeURIComponent(runId)}` : '')
            )
        : undefined,
    [navigate, reportTemplateId]
  )

  /**
   * IS THE PARKED ASK ASKING FOR A METHOD, OR FOR DATA?
   *
   * READ FROM THE PARK, in the backend's own order, and named here so the two cannot drift apart
   * quietly. `resumeRun` in `backend/web/src/kg-agent/workflows-v2/v2-run.service.ts` decides where
   * a `methodSource` goes, and these are its reads in its sequence:
   *
   *   1. `templateId === 'sok-refused'` — the method-ask template, unconditionally. METHOD.
   *   2. `run.input.escalation` present — a gather found nothing and asked for the data. DATA.
   *   3. a `hitl:` slot in the park — a clarification card asked. DATA.
   *   4. otherwise — the intake park, whose whole question is "how is this done here". METHOD.
   *
   * DELIBERATELY BROADER THAN THE BACKEND AT STEP 3, and this is the one place the two differ on
   * purpose. The backend splits a `hitl:` card again, on `supplyOption`: a card that offered to
   * take data gets the file as DATA, and a method or design-context card keeps the file as its
   * ANSWER. The panel cannot see `supplyOption` — it has the park's option labels, not the step —
   * so every `hitl:` park is labelled data here. The residual is a design-context card, where the
   * backend files the words as that card's account and this says "supplied to this run". Narrower
   * than the bug it replaces (every upload said "as the method") and visible in one place if the
   * panel is ever given the card's own supply label.
   *
   * THE ORDER IS LOAD-BEARING ON BOTH SIDES. `intake: true` is stamped by every answer-first start,
   * not only by a method park, so it is true on an escalation park too — reading it first would
   * call a data upload a method, which is the mislabel this exists to fix.
   *
   * AND THE THIRD STEP IS NOT A TEST, deliberately. Once the two DATA parks have returned, every
   * remaining shape is labelled the method: that is what the backend does with a `methodSource`
   * there, and it is the label every upload carried before this function existed, so an
   * unrecognised park keeps the old wording rather than inventing a new claim. Writing it as
   * `input.intake === true ? 'method' : 'method'` mirrored the backend's shape and decided nothing.
   *
   * Interviews use this too. A data-request card exposes the interview picker through "Send a
   * document"; the backend then routes that source as data. Hardcoding `method` on the interview
   * path is how a transcript on that park still said "supplied as the method".
   */
  const suppliedRole = useCallback((): 'method' | 'data' => {
    if (!run) return 'method'
    const input = isRecord(run.input) ? run.input : {}
    // THE METHOD-ASK TEMPLATE WINS OVER EVERY OTHER READ, because it does on the backend too:
    // `parkedForMethod` is `templateId === SOK_REFUSED_TEMPLATE_ID || (!cardPark && intake)`, and
    // the template id is not conjoined with anything. A `sok-refused` run whose `questions` still
    // carry a `hitl:` slot from an earlier park is a run the backend sends to the method lane and
    // the panel used to label data — the inverse of the mislabel this function exists to fix.
    if (run.templateId === 'sok-refused') return 'method'
    if (isRecord(input.escalation)) return 'data'
    const clarification = isRecord(run.progress?.clarification) ? run.progress.clarification : undefined
    const questions = Array.isArray(clarification?.questions) ? clarification.questions : []
    const asksACard = questions.some(
      (raw) => isRecord(raw) && typeof raw.slot === 'string' && raw.slot.startsWith('hitl:')
    )
    if (asksACard) return 'data'
    return 'method'
  }, [run])

  /**
   * Answer the parked ask with a PAST INTERVIEW: read the transcript's text and resume the parked
   * run with it, attributed as an interview rather than a document.
   *
   * This is the route design asked for — "just pick from previous interviews from data catalog"
   * (Putra, 2026-08-21) — and it needs no call to be placed, because a transcript is already a
   * first-class catalog document: `TranscriptArtifactService` mints `type='transcript'` rows, and
   * the picker already lists that type. The live-call variant is a separate seam (the interview
   * substrate builds its own Google Calendar event, so it cannot yet join a link the user pastes).
   *
   * `kind: 'interview'` rather than `'document'` on purpose: the backend records provenance from
   * it, so what an expert SAID must not be filed as a procedure document. The LABEL of that
   * supply — method vs data — is {@link suppliedRole}, same as a file.
   */
  const supplyMethodInterview = useCallback(
    async (documentId: string, title: string): Promise<{ ok: boolean; message: string }> => {
      if (!run) return { ok: false, message: 'There is no parked run to answer.' }
      if (supplyingDocument.current) {
        return { ok: false, message: 'Already sending a method.' }
      }
      supplyingDocument.current = true
      setResuming(true)
      setError(null)
      try {
        const text = (await fetchDocumentRecipeText(documentId)).trim()
        if (!text) {
          // An empty read means the transcript has no body we can use — say so rather than
          // resuming with nothing, which re-parks on the same question.
          const message = `I could not read any usable text from ${title}. Pick another interview, or type the method instead.`
          setError(message)
          return { ok: false, message }
        }
        // SAME READ AS THE DOCUMENT PATH, before the resume — the park is gone from `progress`
        // once the response lands. A data-request card exposes this picker through "Send a
        // document"; the backend then routes the source as data. Hardcoding `method` here is how
        // an interview on that park still said "supplied as the method".
        const role = suppliedRole()
        const resumed = await resumeWorkflowRun(run.id, {
          methodSource: { kind: 'interview', text, title, ref: documentId },
        })
        setRun(resumed)
        setLastSuppliedMethod({
          key: `${run.id}:interview:${documentId}`,
          name: title,
          source: 'interview',
          role,
        })
        // The run was parked (its SSE stream closed) and is running again — re-subscribe, or the
        // panel freezes at the gate for the rest of the run.
        setLiveNonce((n) => n + 1)
        lastResumeError.current = null
        const using = role === 'method' ? 'as the method' : 'for this run'
        return { ok: true, message: `Using ${title} ${using}.` }
      } catch (err) {
        const message = resumeFailureMessage(httpStatus(err), errorMessage(err))
        lastResumeError.current = message
        setError(message)
        return { ok: false, message }
      } finally {
        supplyingDocument.current = false
        setResuming(false)
      }
    },
    [run, suppliedRole]
  )

  /**
   * Send our notetaker into a call the user is hosting, so the intake's "get on a call" option
   * stops being a dead end (it posted its own label, the backend discarded it as a choice rather
   * than an answer, and the run re-parked on the same question — Khang, 2026-08-21).
   *
   * This is the design's flow: the user shares a Google Meet link and the agent joins it (Putra's
   * "Interview / Upload files" frames). It rides the MEETING-BOT substrate, not `/v1/interviews`:
   * that one builds its own Google Calendar event and cannot join a link someone pastes.
   *
   * What happens after the call is deliberately NOT polled here. When the bot leaves, the Python
   * side mints the transcript as a `type='transcript'` catalog document
   * (`meeting_bot/coworker_transcript_capture.py` -> `internal/transcript-artifact`), which is
   * exactly what `supplyMethodInterview` reads. So the hand-back is: join, talk, then pick the
   * transcript. Polling for "is it there yet" would be guessing at a document we can simply let
   * the user choose.
   */
  const startMethodCall = useCallback(
    async (
      meetingUrl: string
    ): Promise<{ ok: boolean; message: string; startedAt?: string; meetingToken?: string }> => {
      const url = meetingUrl.trim()
      // Stamped BEFORE the bot is dispatched: the transcript we wait for afterwards is any
      // transcript created after this instant, and a stamp taken later could miss a short call.
      const startedAt = new Date().toISOString()
      if (!url) return { ok: false, message: 'Paste the meeting link first.' }
      // A bare hostname or a pasted sentence would be sent to the bot as-is and fail inside the
      // vendor call, minutes later and out of sight. Refuse it here instead.
      if (!/^https?:\/\//i.test(url)) {
        return { ok: false, message: 'That does not look like a meeting link — paste the full https:// URL.' }
      }
      setError(null)
      try {
        const { apiService } = await import('@/service/api')
        const { AgentId } = await import('@/constants/agentNames')
        const started = (await apiService.startMeeting(
          url,
          `${window.location.origin}/meeting`,
          // The persona is LOAD-BEARING, not cosmetic. The transcript capture that turns a
          // finished call into a catalog artifact bails out on an anonymous call:
          //
          //   if not digital_coworker_id and not agent_id:
          //       return  # not an AI-agent meeting
          //   — meeting_bot/coworker_transcript_capture.py:159
          //
          // With `agent_id: null` the bot joins, the call happens, and nothing is ever minted —
          // so there is no transcript to attach and the wait would time out every time. Plant
          // Support is also the agent the design names on this card ("AI Plant Support
          // Engineer"), and it is what labels the bot's own turns in the transcript.
          AgentId.PLANT_SUPPORT,
          contextId.trim() || null,
          null,
          false,
          // The call is about THIS workflow's gap, so let the agent read the whole catalog
          // rather than only a board it was never scoped to.
          true
        )) as { meeting_token?: string } | undefined
        return {
          ok: true,
          startedAt,
          // The token IS the transcript's identity: the artifact's docId is
          // `uuidv5(meeting_token, TRANSCRIPT_DOC_NS)`, so carrying it back lets the wait below
          // ask for THIS call's transcript rather than the newest one in the tenant.
          meetingToken: started?.meeting_token,
          message: 'I am joining that call now — talk the method through.',
        }
      } catch (err) {
        const message = errorMessage(err)
        setError(message)
        return { ok: false, message: `I could not join that call: ${message}` }
      }
    },
    [contextId]
  )

  /**
   * Wait for the call to produce its transcript, then hand it back.
   *
   * There is no "the call ended" signal to subscribe to: `/v1/meeting/{token}/events` only
   * reports the bot's own enabled flag, and `/status` the same. What IS observable is the
   * ARTIFACT — when the bot leaves, the Python side mints the transcript as a
   * `type='transcript'` catalog document (`meeting_bot/coworker_transcript_capture.py` ->
   * `internal/transcript-artifact`). So this watches for a transcript created after the call
   * started, which is the thing we actually need rather than a proxy for it.
   *
   * Bounded on purpose: a call nobody joins would otherwise poll for the life of the tab.
   */
  const awaitInterviewTranscript = useCallback(
    async (
      startedAt: string,
      opts?: {
        intervalMs?: number
        timeoutMs?: number
        signal?: AbortSignal
        /**
         * The call's meeting token. Given one, the transcript is identified EXACTLY rather than
         * by "newest since": the artifact's document id is a v5 UUID of this token
         * (`transcriptDocumentId` in `transcript-artifact.service.ts`), so two interviews running
         * in the same tenant cannot be crossed (greptile P1 on #29845).
         */
        meetingToken?: string
      }
    ): Promise<{ id: string; name: string } | null> => {
      const intervalMs = opts?.intervalMs ?? 15_000
      const timeoutMs = opts?.timeoutMs ?? 2 * 60 * 60 * 1000
      const deadline = Date.now() + timeoutMs
      const { apiService } = await import('@/service/api')
      const expectedId = opts?.meetingToken
        ? uuidv5(opts.meetingToken, TRANSCRIPT_DOC_NS)
        : undefined
      while (Date.now() < deadline) {
        if (opts?.signal?.aborted) return null
        try {
          const page = await apiService.listDocsPaginated({
            type: 'transcript',
            created_after: startedAt,
            sort_by: 'created_at',
            sort_direction: 'desc',
            // A page rather than a single row: with a token we are looking for one specific
            // document, and a concurrent interview could otherwise occupy the only slot.
            page_size: expectedId ? 25 : 1,
          })
          const docs = (page?.documents ?? []) as Array<{
            id?: string
            name?: string
            original_filename?: string
          }>
          // With a token, only THIS call's artifact will do. Without one (older callers), fall
          // back to the newest — the previous behaviour, and the reason the fallback is explicit.
          const doc = expectedId ? docs.find((d) => d.id === expectedId) : docs[0]
          if (doc?.id) {
            return { id: doc.id, name: doc.name ?? doc.original_filename ?? 'Interview transcript' }
          }
        } catch {
          // A failed poll is not a failed call — keep waiting rather than abandoning a call
          // that is still running.
        }
        // Clamp the wait to what is left: sleeping a full interval near the deadline made the
        // effective timeout `timeoutMs + intervalMs` (Graphite on #29845).
        const remaining = deadline - Date.now()
        if (remaining <= 0) break
        await new Promise((resolve) => setTimeout(resolve, Math.min(intervalMs, remaining)))
      }
      return null
    },
    []
  )

  const cancelRun = useCallback(async () => {
    if (!run) return
    setLoading(true)
    setError(null)
    try {
      const cancelled = await cancelWorkflowRun(run.id)
      setRun(cancelled)
      await loadRun(run.id)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [loadRun, run])

  // Advance a run parked in `awaiting_approval` (the pre-run HITL gate). The
  // endpoint existed since the gate shipped — the UI affordance did not, so a
  // gated run was unadvanceable from the product.
  const approveRun = useCallback(async () => {
    if (!run) return
    setResuming(true)
    setError(null)
    try {
      const approved = await approveWorkflowRun(run.id)
      setRun(approved)
      // Re-open the live-progress stream (the run was parked, SSE closed).
      setLiveNonce((n) => n + 1)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setResuming(false)
    }
  }, [run])

  /**
   * Re-run an unsuccessful run. v1 resumes from the failed step; v2 re-runs the stored plan.
   * Button copy: `retryFailedRunCopy`.
   */
  const retryRun = useCallback(async () => {
    if (!run) return
    setResuming(true)
    setError(null)
    try {
      const retried = await retryWorkflowRun(run.id)
      setRun(retried)
      setLiveNonce((n) => n + 1)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setResuming(false)
    }
  }, [run])

  /** Resume a `paused` run (operator pause) — same re-subscribe dance as approve. */
  const unpauseRun = useCallback(async () => {
    if (!run) return
    setResuming(true)
    setError(null)
    try {
      const resumed = await unpauseWorkflowRun(run.id)
      setRun(resumed)
      setLiveNonce((n) => n + 1)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setResuming(false)
    }
  }, [run])

  const lastResumeError = useRef<string | null>(null)
  // Re-entry: `resuming` state is not yet true on a second click that lands before the first
  // `setResuming(true)` flushes. The panel also gates on `busy`, but this handler is callable
  // without that (keyboard, a second file-input change).
  const supplyingDocument = useRef(false)
  /**
   * What most recently answered the intake, so the transcript can SHOW it regardless of which
   * affordance was used. The chat used to push that card from the click handler, which meant a
   * second entry point (Add Files, while a method ask is parked) resumed the run with nothing on
   * screen to say what it was now following. Recorded here; rendered by the panel.
   */
  const [lastSuppliedMethod, setLastSuppliedMethod] = useState<{
    key: string
    name: string
    source: 'document' | 'interview'
    /**
     * WHAT THE FILE WAS FOR — the method the run will follow, or data it will read.
     *
     * The card said "supplied as the method" for every upload, including one attached to a
     * clarification card that had asked for downtime records. The reader was told their data had
     * become the procedure, which is both wrong and alarming, and it named the one lane the file
     * did NOT take.
     */
    role: 'method' | 'data'
  } | null>(null)

  /**
   * Answer the intake with a METHOD DOCUMENT: extract its text, then resume the parked run with it.
   *
   * Two things this deliberately is not. It is not `apiService.uploadFile` — that enqueues the
   * ingestion pipeline and the caller polls for minutes before any text exists, for a document that
   * is read once. And it is not a message: the text goes to the backend's `methodSource` seam, which
   * has accepted it since the intake shipped and has never had a producer, so today the "Send a
   * document" button posts its own label and the run re-parks on the same question.
   *
   * The extracted text is held only for this call. Nothing writes it to the catalog, and nothing
   * survives the conversation.
   */
  const supplyMethodDocument = useCallback(
    async (file: File): Promise<{ ok: boolean; message: string }> => {
      if (!run) return { ok: false, message: 'There is no parked run to answer.' }
      if (supplyingDocument.current) {
        return { ok: false, message: 'Already sending a document.' }
      }
      supplyingDocument.current = true
      setResuming(true)
      setError(null)
      try {
        const extracted = await extractDocumentText(file)
        const text = extracted.text.trim()
        if (!text) {
          // A scanned PDF has no text layer and this route does not OCR — say so plainly rather
          // than resuming with an empty method, which would re-park on the same question.
          const message =
            'I could not read any text from that file. If it is a scan, try a text PDF, a Word file, or type the method instead.'
          setError(message)
          return { ok: false, message }
        }
        // READ BEFORE THE RESUME. The resume advances the run — the park that asked is gone from
        // `progress` by the time the response lands — so the question "what was this file for" can
        // only be answered against the state that was on screen when the reader attached it.
        const role = suppliedRole()
        const resumed = await resumeWorkflowRun(run.id, {
          methodSource: { kind: 'document', text, title: file.name },
        })
        setRun(resumed)
        setLastSuppliedMethod({
          // Name + run is enough to key it: the same file supplied twice to the SAME park is one
          // answer, and a local file has no catalog id to use instead.
          key: `${run.id}:document:${file.name}`,
          name: file.name,
          source: 'document',
          role,
        })
        // The run was parked (its SSE stream closed) and is running again — re-subscribe, or the
        // panel freezes at the gate for the rest of the run.
        setLiveNonce((n) => n + 1)
        lastResumeError.current = null
        const using = role === 'method' ? 'as the method' : 'for this run'
        return {
          ok: true,
          message: extracted.truncated
            ? `Using ${file.name} ${using} (only the first part of it — the document is long).`
            : `Using ${file.name} ${using}.`,
        }
      } catch (err) {
        const message = resumeFailureMessage(httpStatus(err), errorMessage(err))
        lastResumeError.current = message
        setError(message)
        return { ok: false, message }
      } finally {
        supplyingDocument.current = false
        setResuming(false)
      }
    },
    [run, suppliedRole]
  )

  /**
   * Resume the parked run, and hand the CALLER the run it resumed into.
   *
   * It returned `true`/`false` — resumed, or did not — and the panel printed *"Thanks. I will use
   * that clarification and continue the troubleshooting run."* on every truthy result. That is
   * wrong exactly when the resume BOUND the answer and re-parked on the next question, which is
   * v2's normal shape: a resume binds one slot and re-parks on what is left, so a three-question
   * intake is three 200s that each say "continuing" while the same panel keeps asking. Dev run
   * `19ef7174` (2026-09-04): two typed replies, two acknowledgements, the card still asking.
   *
   * The run view already came back from `resumeWorkflowRun` and was already being written to
   * state; it was only the return value that threw the answer away. `null` on failure keeps the
   * caller's truthiness test working.
   */
  const resumeRun = useCallback(
    async (answerOverride?: string): Promise<WorkflowRunView | null> => {
      if (!run) return null
      const answer = (answerOverride ?? clarificationAnswer).trim()
      if (!answer) {
        const msg = 'Clarification answer is required.'
        lastResumeError.current = msg
        setError(msg)
        return null
      }
      setResuming(true)
      setError(null)
      try {
        // Derive the clarification step id from the run rather than hardcoding
        // 'ask-clarification'. Agent-initiated pauses park the ASKING step
        // itself (any kind) in waiting_for_user and persist its id on
        // run.progress; dedicated clarification steps and the legacy
        // 'ask-clarification' id are kept as fallbacks for older runs.
        const progressStepId = (run.progress as { clarificationStepId?: unknown } | undefined)
          ?.clarificationStepId
        const clarificationStepId =
          (typeof progressStepId === 'string' && progressStepId ? progressStepId : undefined) ??
          // Deliberately kind-agnostic: agent-initiated pauses park the asking
          // AGENTIC step in waiting_for_user, so filtering on
          // kind === 'clarification' here would skip exactly the step that
          // asked. Post-cutover runs are covered by progressStepId above; this
          // is the fallback when progress is missing.
          steps.find((step) => step.status === 'waiting_for_user')?.stepId ??
          steps.find((step) => step.kind === 'clarification')?.stepId ??
          'ask-clarification'
        // A v2 park binds by SLOT, not by step id. Keying a v2 answer by the step id
        // (`bind`) matches no slot, so the run re-parks on the same question forever —
        // see `getNextClarification`.
        const nextSlot = getNextClarification(run).slot
        const resumed = await resumeWorkflowRun(run.id, {
          clarificationAnswers: nextSlot
            ? { [nextSlot]: answer }
            : { [clarificationStepId]: answer },
        })
        setRun(resumed)
        setClarificationAnswer('')
        // Re-open the live-progress stream: the run was parked (SSE closed) and
        // is now running again, so bump the nonce to re-subscribe + keep the UI
        // updating through the rest of the run instead of freezing at the gate.
        setLiveNonce((n) => n + 1)
        lastResumeError.current = null
        return resumed
      } catch (err) {
        // 400 / 403 / 409 are actionable ("rephrase", "not yours", "lost the
        // claim") — a generic "could not resume" hid a scanner 400 as a
        // mysterious failure (soft #28668).
        const msg = resumeFailureMessage(httpStatus(err), errorMessage(err))
        lastResumeError.current = msg
        setError(msg)
        return null
      } finally {
        setResuming(false)
      }
    },
    [clarificationAnswer, run, steps]
  )

  const selectGraphNode = useCallback((node: WorkflowGraphNodeData | null) => {
    // Tool nodes open the tool-call dialog; every other node (run steps, step
    // outputs, draft phases) opens the node-details dialog. Clear the other
    // selection on every pick so the two dialogs stay mutually exclusive.
    if (node?.toolCall) {
      setSelectedToolCall(node.toolCall)
      setSelectedGraphNode(null)
      return
    }
    setSelectedToolCall(null)
    setSelectedGraphNode(node)
  }, [])

  // The selection snapshots the node at click time, but polling replaces
  // `steps` every 2s — refresh the embedded step run so an open dialog tracks
  // the live status/output instead of freezing at the click-time state.
  // A corpus/question plan rewrites the card ids wholesale mid-run; if the
  // selected id is gone, drop the selection rather than pinning a card that
  // is no longer on the canvas (review on #30006).
  const liveSelectedGraphNode = useMemo(() => {
    if (!selectedGraphNode) return null
    const selectedStep = selectedGraphNode.step
    if (selectedStep) {
      const liveStep =
        steps.find((step) => step.id === selectedStep.id) ??
        steps.find((step) => step.stepId === selectedStep.stepId)
      if (!liveStep) return null
      if (liveStep === selectedGraphNode.step) return selectedGraphNode
      return {
        ...selectedGraphNode,
        step: liveStep,
        status: liveStep.status,
        subtitle: liveStep.status,
      }
    }
    const phaseId = selectedGraphNode.draftPhase?.id
    if (phaseId && planPreview) {
      const phases = planPreview.phases ?? []
      if (!phases.some((phase) => phase.id === phaseId)) return null
    }
    return selectedGraphNode
  }, [selectedGraphNode, steps, planPreview])

  // Only surface sources that belong to the run on screen. `run?.id` is the run
  // actually loaded; `runId` covers the window before its first GET lands.
  const currentRunId = run?.id ?? runId
  const resolvedSources =
    resolvedSourcesEntry && resolvedSourcesEntry.runId === currentRunId
      ? resolvedSourcesEntry.sources
      : null

  return {
    runId,
    templateId,
    openedTemplateId,
    /** Authored (persist+publish on Run) vs built-in preview (run by id). */
    draftIsAuthored,
    /** Opened workflow published? (null = unknown/fresh builder.) */
    openedPublished,
    problem,
    setProblem,
    workflowPrompt,
    setWorkflowPrompt,
    existingConversationId,
    setExistingConversationId,
    contextId,
    setContextId,
    contextType,
    setContextType,
    timeWindowStart,
    setTimeWindowStart,
    timeWindowEnd,
    setTimeWindowEnd,
    scopeTags,
    setScopeTags,
    scopeEquipment,
    setScopeEquipment,
    scopeDocumentIds,
    setScopeDocumentIds,
    scopeBoardId,
    setScopeBoardId,
    engine,
    refreshEngine,
    verification,
    setVerification,
    readinessWarnings,
    draft,
    planPreview,
    run,
    /**
     * A run is named in the URL but hasn't loaded yet (and isn't known-gone).
     * Bridges the gap between startRun's navigate and the first successful
     * load — without it the Run pill flipped back to clickable for a beat
     * ("started, instantly stopped, started again"), or for good when the
     * first load failed on a struggling pod.
     */
    runPending: Boolean(runId) && !run && !runMissing,
    steps,
    selectedToolCall,
    setSelectedToolCall,
    selectedGraphNode: liveSelectedGraphNode,
    clarificationAnswer,
    setClarificationAnswer,
    clarificationQuestion: run ? getClarificationQuestion(run) : '',
    // THE CARD'S IDENTITY beside its wording — see `nextAskSlotFor`. The acknowledgement needs to
    // know whether a re-park came back on the SAME card, and the prompt is expected to change when
    // it does.
    clarificationSlot: run ? nextAskSlotFor(run) : undefined,
    clarificationOptions: run ? getClarificationOptions(run) : [],
    // 'review_checkpoint' when the park is an explicit HITL review gate —
    // the chat renders a different header for it than a real question.
    clarificationKind: run ? getClarificationKind(run) : '',
    loading,
    starting,
    resuming,
    error,
    lastResumeError,
    generateDraft,
    generateDraftFromDocument,
    openReport,
    resolvedSources,
    startRun,
    cancelRun,
    approveRun,
    retryRun,
    unpauseRun,
    resumeRun,
    supplyMethodDocument,
    supplyMethodInterview,
    lastSuppliedMethod,
    /**
     * What the park in front of the reader is asking FOR — the same read
     * {@link suppliedRole} makes after an upload, made before it.
     *
     * #31304 fixed the label on the artifact card once the file was in. The affordances that ask
     * for it were left on the method wording, so a park asking for downtime records offered
     * "Attach a file that describes the process", "or use a past interview" and "describe the
     * method below" — three method sentences on a data ask (dev runs `3819cee5` / `c74ba9a5`).
     */
    askRole: suppliedRole(),
    startMethodCall,
    awaitInterviewTranscript,
    refreshRun,
    selectGraphNode,
  }
}

export type TroubleshootingWorkflowController = ReturnType<
  typeof useTroubleshootingWorkflow
>

function getClarificationKind(run: WorkflowRunView): string {
  const clarification = isRecord(run.progress?.clarification)
    ? run.progress.clarification
    : undefined
  return typeof clarification?.kind === 'string' ? clarification.kind : ''
}

/**
 * The next thing the run is waiting to be told, and — for a v2 park — WHICH SLOT the
 * answer belongs to.
 *
 * Two park vocabularies meet here, and reading only one of them was a hard blocker:
 *
 *  • **v1** writes `clarification.question`, one free-text answer keyed by the parked
 *    STEP id. That is what this hook understood.
 *  • **v2** writes `clarification.questions[]` — `{ slot, kind, prompt }` — and its resume
 *    binds by SLOT NAME. A v2 park therefore rendered the generic fallback below (the
 *    engineer never saw "What should be gathered?"), and the answer arrived keyed by the
 *    step id `bind`, matched no slot, and the run RE-PARKED forever.
 *
 * Answered ONE AT A TIME rather than as a form, because that is what the chat surface is
 * and because v2 already supports it: a resume carrying a partial answer binds what it got
 * and re-parks on what is left, so each round asks the next outstanding slot. No backend
 * change — the sequence falls out of the contract that already exists.
 */
interface NextClarification {
  /** Present for a v2 park — the answer must be keyed by this. Absent ⇒ key by step id. */
  slot?: string
  prompt: string
}

function getNextClarification(run: WorkflowRunView): NextClarification {
  const clarification = isRecord(run.progress?.clarification)
    ? run.progress.clarification
    : undefined

  // v2: the FIRST still-outstanding slot. `parkClarify` rewrites this array on every
  // re-park with only what remains, so index 0 is always the next question.
  const questions = Array.isArray(clarification?.questions) ? clarification.questions : []
  for (const raw of questions) {
    if (!isRecord(raw)) continue
    // The slot string is the OPAQUE bind key — do not trim it. Resume matches
    // `s.slot in bound` verbatim; a trimmed key on a padded slot re-parks forever
    // (greptile P1 on #28668). Whitespace-only is still not a slot.
    const slot = typeof raw.slot === 'string' ? raw.slot : ''
    if (slot.trim() === '') continue
    const prompt = typeof raw.prompt === 'string' && raw.prompt.trim() ? raw.prompt.trim() : ''
    return {
      slot,
      // A slot with no authored prompt still has to be askable, so fall back to naming it
      // rather than showing the generic sentence — "which tag?" beats "provide context".
      // "Which data needed should I use?" is stiff; tag-shaped names keep Which, others ask What.
      prompt: prompt || fallbackPromptForSlot(slot.trim()),
    }
  }

  // v1: one question, answered against the parked step id.
  if (typeof clarification?.question === 'string' && clarification.question.trim()) {
    return { prompt: clarification.question }
  }
  return { prompt: 'Please provide the missing troubleshooting context required to continue.' }
}

function getClarificationQuestion(run: WorkflowRunView): string {
  return getNextClarification(run).prompt
}

/**
 * The question a run is asking NOW, read off A RUN VIEW THE CALLER ALREADY HOLDS.
 *
 * Exported for the resume acknowledgement: `clarificationQuestion` on this hook is derived from
 * the hook's own `run` state, which has not re-rendered yet at the moment the reply is
 * acknowledged — reading it there would quote the question that was just answered.
 *
 * THE ARGUMENT IS THE POINT, and it is what distinguishes this from `clarificationQuestion`:
 * that one is hook-derived state, this one is a pure read of whatever view you pass — normally the
 * one `resumeRun` just returned. Confusing the two IS the bug this PR fixes, so a caller reaching
 * for the shorter name because it needs no argument is reaching for the stale answer. If you have
 * a fresher run view than the hook's, this is the reader; otherwise they agree.
 */
export function nextAskFor(run: WorkflowRunView): string {
  return getNextClarification(run).prompt
}

/**
 * WHICH CARD a run is parked on — its slot, which is the card's IDENTITY.
 *
 * Exported beside {@link nextAskFor} because "did the run come back on the same card?" is a
 * question about identity and not about wording. The prompt is expected to CHANGE on a re-park —
 * the supply park appends *"Waiting for that data…"*, the period card's re-ask prepends *"I read 0
 * dates in that reply"* — so comparing prompts answered it wrongly in both directions: the same
 * card with changed wording read as a new one, and two DIFFERENT slots that happen to share
 * wording read as the same one (greptile + review on #31166).
 *
 * `getNextClarification`'s own comment says why the slot is the stable half: *"the OPAQUE bind key
 * — do not trim it. Resume matches `s.slot in bound` verbatim."* A v1 park has none, which is why
 * this can be `undefined`; there the caller falls back to comparing prompts, which is all a v1
 * park ever had.
 */
export function nextAskSlotFor(run: WorkflowRunView): string | undefined {
  return getNextClarification(run).slot
}

/** What the panel says when `resumeRun` could not resume at all. */
export const RESUME_FAILED_FALLBACK =
  'I could not resume the workflow. Check the error above and try again.'

/**
 * WHAT TO SAY AFTER A REPLY IS SUBMITTED — read off the run the resume RETURNED.
 *
 * The panel used to say "Thanks. I will use that clarification and continue the troubleshooting
 * run." for every status that was not `waiting_for_user`, on the reasoning that anything else means
 * the run went on. It does mean that, and it does not mean the asking is over: a plan that opens
 * with two cards — `ask_scope` then `ask_period`, which is exactly the demo question's shape —
 * comes back `queued` from the first reply and parks on the second a moment later. So the reader
 * was thanked for a clarification, told the run was continuing, and then handed another card. Two
 * of those in a row reads as an input that did nothing.
 *
 * `queued` and `running` are therefore "still working, and it may still ask" rather than "done
 * asking". The wording promises nothing about whether another card is coming, because at this
 * moment nobody knows — the next suspend has not happened yet.
 *
 * NOT `workflow.clarificationQuestion`: that is derived from the hook's `run` state, which has not
 * re-rendered when this is composed, so it would quote the question just answered. Same reason
 * {@link nextAskFor} takes the view as an argument.
 */
export function resumeAcknowledgement(
  resumed: WorkflowRunView | null | undefined,
  resumeError?: string | null,
  /**
   * WHICH CARD the run was parked on before this reply — its slot, so a re-park on the same card
   * can be told apart. The prompt is deliberately not used: see {@link nextAskSlotFor}.
   */
  askedBefore?: { slot?: string; prompt?: string },
): string {
  // `||`, NOT `??`, and the difference is one value: an EMPTY resume error would print an empty
  // assistant block where the fallback belongs. It cannot hold `''` today — every assignment is a
  // literal, `null`, or `resumeFailureMessage`, which ends each branch on `raw || <fallback>` — but
  // that invariant lives two functions away in the WRITER, and this reader is what breaks if it
  // moves. The same class this stack has fixed repeatedly: a reader that outlives its writer's
  // guarantee (review on #31121).
  if (!resumed) return resumeError || RESUME_FAILED_FALLBACK
  if (resumed.status === 'waiting_for_user') {
    // RE-PARKED ON THE SAME CARD IS NOT "NEXT". A reader who said they would send the data gets the
    // card back, waiting for the file — and "Noted. Next: …" in front of the question they just
    // answered reads as the run having ignored them, which is the reading this stack keeps fixing.
    // The prompt already says what it is waiting for, so the acknowledgement gets out of its way.
    const asking = nextAskFor(resumed)
    const askingSlot = nextAskSlotFor(resumed)
    // THE SLOT, when both sides have one — the card's identity rather than its wording. Two
    // different cards can be worded identically, and the same card is EXPECTED to reword itself on
    // a re-park (#31165 appends the waiting line), so a prompt comparison is wrong in both
    // directions. A v1 park carries no slot; there the prompt is all there ever was.
    const sameCard =
      askedBefore === undefined
        ? false
        : askingSlot !== undefined && askedBefore.slot !== undefined
          ? askedBefore.slot === askingSlot
          : askedBefore.prompt === asking
    if (sameCard) return 'Noted.'
    return `Noted. Next: ${asking}`
  }
  if (resumed.status === 'queued' || resumed.status === 'running') {
    return 'Noted. The run is continuing — if it needs anything else, the next card will ask.'
  }
  return 'Thanks. I will use that clarification and continue the troubleshooting run.'
}

/** No-prompt fallback: tag-shaped names keep "Which … should I use?"; others ask What. */
function fallbackPromptForSlot(slot: string): string {
  const words = slot.replace(/_/g, ' ').replace(/\s+/g, ' ').trim()
  if (/(?:^|_)tag$/.test(slot)) {
    return `Which ${words} should I use?`
  }
  if (/_needed$/.test(slot)) {
    return `What ${words.replace(/ needed$/, '')} is needed?`
  }
  return `What is the ${words}?`
}

/**
 * Agent-suggested answer options for the active clarification (the optional
 * `options` the request_clarification tool can emit — e.g. candidate tags, or
 * document names when the ask is about document scope). The builder renders
 * these as one-click chips alongside a free-text "type your own" fallback.
 */
function getClarificationOptions(run: WorkflowRunView): string[] {
  const clarification = isRecord(run.progress?.clarification)
    ? run.progress.clarification
    : undefined
  const options = clarification?.options
  if (!Array.isArray(options)) return []
  return options.filter((o): o is string => typeof o === 'string' && o.trim().length > 0)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.'
}

/**
 * Chat/banner copy for a failed v2 resume. Status is what the controller maps
 * (`V2ClarificationRejectedError` → 400, `V2RunNotOwnedError` → 403,
 * `V2ResumeClaimLostError` / `V2RunNotResumableError` → 409).
 */
function resumeFailureMessage(status: number | undefined, raw: string): string {
  if (status === 400) {
    return raw.includes('prompt-injection')
      ? 'That answer was rejected as possible prompt injection. Rephrase it and try again.'
      : raw || 'That answer was rejected. Rephrase it and try again.'
  }
  if (status === 403) {
    return 'You cannot resume this run — it belongs to someone else.'
  }
  if (status === 409) {
    return raw || 'This run cannot be resumed right now. Retry, or start a new run.'
  }
  return raw || 'I could not resume the workflow. Check the error above and try again.'
}

/**
 * HTTP status off an error thrown by `apiService.fetchOrError`, which
 * attaches an axios-shaped `{ response: { status } }`. Returns undefined
 * for non-HTTP errors (network failures, programming errors).
 */
/**
 * Version-agnostic content identity for a definition. The `version` field is
 * excluded on purpose: the server mints it (auto-save forks bump it), so two
 * copies of the same authored content may legitimately differ only there.
 */
function workflowContentFingerprint(definition: WorkflowDefinition): string {
  const { version: _version, ...content } = definition
  return JSON.stringify(content)
}

function httpStatus(error: unknown): number | undefined {
  const status = (error as { response?: { status?: unknown } } | null)?.response
    ?.status
  return typeof status === 'number' ? status : undefined
}
